<template>
  <section class="merchant-dashboard">
    <span
      id="disabled-send-new-wrapper"
      class="d-inline-block mb-3">
      <b-button
        :disabled="!sentNotificationsLoaded || bulkMessageRunningState || progressBarTimeout"
        class="btn btn-add-item btn-blue btn-round"
        @click="openSendNotificationModal()">Send New Notification</b-button>
    </span>
    <b-tooltip
      v-if="bulkMessageRunningState"
      target="disabled-send-new-wrapper"
      placement="right">Wait until currently running campaign is sent</b-tooltip>
    <div class="table-tools d-flex align-items-center justify-content-between flex-column flex-md-row">
      <b-col
        md="6"
        sm="12"
        class="px-0">
        <b-col
          md="8"
          sm="12"
          class="float-left px-0 mb-3">
          <b-input-group class="search-filter">
            <b-input
              v-model="searchQuery"
              type="text"
              size="sm"
              autocomplete="off"
              placeholder="Search"
              @change="searchNotifications"/>
            <b-input-group-text
              slot="append"
              @click="searchNotifications">
              <img
                src="~@/assets/img/search-icon-light.svg"
                alt="Search"
                width="14px"
                height="14px">
            </b-input-group-text>
          </b-input-group>
        </b-col>
        <b-col
          md="4"
          sm="12"
          class="float-left px-0 mb-3 px-md-3">
          <b-dropdown
            :clearable="false"
            toggle-class="actions-toggle"
            menu-class="actions-menu"
            right
            no-flip
            class="dashboard-actions">
            <template slot="button-content">
              {{ selectedStatus || 'All Notifications' }}
            </template>
            <b-dropdown-item
              @click="selectStatus('')">All Notifications</b-dropdown-item>
            <b-dropdown-item
              v-for="status in statuses"
              :key="status.id"
              :class="{active: selectedStatus === status.id}"
              @click="selectStatus(status)">{{ status.name }}</b-dropdown-item>
          </b-dropdown>
        </b-col>
      </b-col>
      <b-col
        md="6"
        sm="12"
        class="px-0">
        <DatePicker
          ref="dateRange"
          class="text-md-right text-sm-left"
          @datesChanged="filterDates"/>
      </b-col>
    </div>

    <b-table
      :fields="fields"
      :items="sentNotifications.data"
      show-empty
      responsive
      class="merchant-dashboard__table scroll-area-horizontal"
      tbody-tr-class="show-hand"
      details-td-class="campaign-details"
      @row-clicked="redirectToDetails">

      <!-- Name column -->
      <template v-slot:cell(name)="data">
        <span v-if="data.item.contact.phone">{{ data.item.contact.name }}</span>
        <span v-else>Campaign</span>
      </template>

      <!-- Phone column -->
      <template v-slot:cell(phone)="data">
        <span v-if="data.item.contact.phone">{{ data.item.contact.phone }}</span>
        <span
          v-else
          class="tag-item">{{ data.item.bulk_res_data.all }} numbers</span>
      </template>

      <!-- Template Name column -->
      <template v-slot:cell(keyword)="data">
        {{ data.item.template_message.keyword }}
      </template>

      <!-- Status column -->
      <template v-slot:cell(status)="data">
        <span v-if="data.item.status === 'sending' && data.item.id === initiatedBulkUUID">{{ progress }}%</span>
        <span
          v-else
          :id="`popover-details-${data.item.id}`"
          :class="setStatusClass(data.item.status)"
          class="tag-item">
          {{ data.item.status }}</span>
        <b-popover
          :target="`popover-details-${data.item.id}`"
          placement="right"
          triggers="hover focus"
          :disabled="!data.item.bulk">
          <template v-slot:title>
            Stats
          </template>
          <div>
            <p class="text-secondary">
              <icon name="paper-plane"/>{{ sent(data.item.bulk_res_data) }} sent
            </p>
            <p class="text-info">
              <b-icon-flag-fill class="mr-2"/>{{ delivered(data.item.bulk_res_data) }} delivered
            </p>
            <p class="text-success">
              <b-icon-eye class="mr-2"/>{{ data.item.bulk_res_data.read }} read
            </p>
            <p class="text-danger">
              <b-icon-alert-circle class="mr-2"/>{{ data.item.bulk_res_data.failed }} failed
            </p>
          </div>
        </b-popover>
      </template>

      <!-- Sent at column -->
      <template v-slot:cell(timestamp)="data">
        {{ data.item.timestamp | dateShort }}
      </template>

      <!-- Actions column -->
      <template v-slot:cell(actions)="data">
        <b-dropdown
          right
          no-caret
          class="actions">
          <!-- button -->
          <template v-slot:button-content>
            <icon name="ellipsis-h"/>
          </template>
          <!-- Items -->
          <b-dropdown-item @click="showResendNotificationModal(data.item)">
            Send Again
          </b-dropdown-item>
          <b-dropdown-item v-if="!data.item.bulk">
            <router-link
              :to="{ name: 'ContactProfile', params: { contact_id: data.item.contact.id } }"
              tag="span">
              Open Contact
            </router-link>
          </b-dropdown-item>
        </b-dropdown>
      </template>

      <!-- No data content -->
      <template v-slot:empty="">
        <div
          v-if="!sentNotifications.length && sentNotificationsLoaded"
          slot="empty"
          class="no-data text-center">
          <img
            src="~@/assets/img/no-data.svg"
            alt="No Data">
          <!--eslint-disable vue/no-v-html-->
          <span
            class="d-block mt-2"
            v-html="noDataMessage"/>
        </div>
        <div
          v-if="!sentNotifications.length && !sentNotificationsLoaded"
          slot="empty"
          class="loader">
          <div class="loading-circle"/>
        </div>
      </template>

      <!-- Details section for progress or message content -->
      <template v-slot:row-details="row">
        <b-progress
          v-if="row.item.bulk"
          :value="progress"
          height="2px"/>
        <div v-else>
          <span>Message: </span>
          <p>
            {{ row.item.template_message.text }}
          </p>
          <template v-if="row.item.template_message.attachment">
            <span>Attachment: </span>
            <a :href="row.item.template_message.attachment" target="_blank">
              {{ getMediaName(row.item.template_message.attachment) }}
            </a>
          </template>
        </div>
      </template>
    </b-table>

    <div class="paging mb-5 clearfix">
      <p class="float-left pages">{{ sentNotifications.meta.current_page }} of {{ sentNotifications.meta.last_page }}</p>
      <button
        :disabled="page === sentNotifications.meta.last_page"
        class="btn btn-default btn-paging float-right"
        @click="nextPage">Next</button>
      <button
        :disabled="page === 1"
        class="btn btn-default btn-paging float-right"
        @click="previousPage">Back</button>
      <b-input
        v-model="sentNotifications.meta.current_page"
        :disabled="sentNotifications.meta.last_page === 1"
        type="number"
        min="1"
        class="page-input"
        placeholder="1"
        @change="changePage"/>
    </div>
    <InfoModal
      ref="permissionInfoModal"
      message="You don't have permission to do this action."/>
    <NotificationSendModal
      ref="notificationSendModal"
      @sent="refreshNotifications"
      @bulkSent="refreshNotifications"/>
  </section>
</template>

<style lang="scss">
.merchant-dashboard__table .table tr.b-table-details {
  td {
    padding-left: 0!important;
    padding-right: 0!important;
    padding-top: 0!important;
    padding-bottom: 0!important;

    [class*="popover-details"] {
      cursor: auto;
    }

    .progress-bar {
      background: $bluePrimary;
    }
  }

  .campaign-details {
    background: $lightGray;
  }
}

.popover .fa-icon {
  margin-right: 8px;
  width: 14px;
  height: 9px;
}
</style>

<script>
import { mapGetters, mapActions } from 'vuex';
import InfoModal from '../../common/modals/InfoModal';
import DatePicker from '../../common/DatePicker';
import NotificationSendModal from './modals/NotificationSendModal';
import { getMediaName } from '../../../helpers/mediaHelpers';

export default {
  name: 'SentNotifications',
  components: {
    InfoModal,
    DatePicker,
    NotificationSendModal,
  },
  data() {
    return {
      fields: [
        'name',
        {
          key: 'phone',
          label: 'Phone Number',
        },
        {
          key: 'keyword',
          label: 'Template Name',
        },
        'status',
        {
          key: 'sent_at',
          label: 'Sent At',
          class: 'text-right',
        },
        {
          key: 'actions',
          class: 'text-right',
        },
      ],
      statuses: [
        {
          id: 'sent',
          name: 'Sent',
        },
        {
          id: 'delivered',
          name: 'Delivered',
        },
        {
          id: 'read',
          name: 'Read',
        },
        {
          id: 'failed',
          name: 'Failed',
        },
      ],
      dateRange: {
        startDate: '',
        endDate: '',
      },
      searchQuery: '',
      selectedStatus: '',
      page: 1,
      limit: 10,
      initiatedBulkUUID: '',
      progressBarTimeout: false,
    };
  },
  computed: {
    ...mapGetters({
      sentNotifications: 'notification/sentNotifications',
      sentNotificationsLoaded: 'notification/sentNotificationsLoaded',
      bulkSendProgress: 'pusher/bulkSendProgress',
      bulkMessageRunningState: 'pusher/bulkMessageRunningState',
    }),
    noDataMessage() {
      if (this.searchQuery) {
        return `We couldn't find anything for <b>${this.searchQuery}</b>.`;
      }

      if (this.selectedStatus) {
        return `You don't have any notification with <b>${this.selectedStatus}</b> status.`;
      }

      if (this.dateRange.startDate) {
        return 'We couldn\'t find anything for selected dates.';
      }

      return 'You haven’t sent any notification yet.';
    },
    hasSendNotificationPermission() {
      return this.$hasPermissionInOrganisation('send_notifications');
    },
    progress() {
      let counter = 0;
      const total = this.bulkSendProgress.all || 0;
      counter += this.bulkSendProgress.sent || 0;
      counter += this.bulkSendProgress.delivered || 0;
      counter += this.bulkSendProgress.read || 0;
      counter += this.bulkSendProgress.failed || 0;
      const progress = total !== 0 ? counter / total * 100 : 0;
      return progress.toFixed(0);
    },
  },
  watch: {
    // Check if currently running bulk message notification is in the table and show progress for it.
    sentNotifications() {
      this.showProgressBar();
    },
    bulkMessageRunningState(state) {
      if (!state) {
        // timeout is here to allow the progressbar animation to reach 100%
        this.progressBarTimeout = true;
        setTimeout(() => {
          this.loadSentNotifications();
          this.progressBarTimeout = false;
          this.initiatedBulkUUID = '';
        }, 1000);
      }
    },
  },
  mounted() {
    this.loadSentNotifications();
    this.initiatedBulkUUID = localStorage.getItem(`_c_gm_pf_ns.${this.$route.params.merchant_id}`);
  },
  methods: {
    ...mapActions({
      fetchSentNotifications: 'notification/fetchSentNotifications',
    }),
    getMediaName,
    loadSentNotifications() {
      this.fetchSentNotifications({
        merchant_id: this.$route.params.merchant_id,
        data: {
          limit: this.limit,
          page: this.page,
          status: this.selectedStatus,
          search_query: this.searchQuery,
          start_date: this.dateRange.startDate,
          end_date: this.dateRange.endDate,
        },
      });
    },
    selectStatus(status) {
      this.selectedStatus = status.id;
      this.page = 1;
      this.loadSentNotifications();
    },
    filterDates(dates) {
      if (dates.length) {
        this.dateRange = {
          startDate: dates[0].unix(),
          endDate: dates[1].unix(),
        };
      } else {
        this.dateRange = {
          startDate: '',
          endDate: '',
        };
      }

      this.page = 1;
      this.loadSentNotifications();
    },
    refreshNotifications(e) {
      if (e.bulkSendUUID) {
        this.initiatedBulkUUID = e.bulkSendUUID;
      }
      this.dateRange = {
        startDate: '',
        endDate: '',
      };
      this.searchQuery = '';
      this.selectedStatus = '';
      this.page = 1;
      this.loadSentNotifications();
    },
    searchNotifications() {
      this.page = 1;
      this.$refs.dateRange.clear(false);
      this.loadSentNotifications();
    },
    changePage(page) {
      page = parseInt(page, 10);
      if (page > this.sentNotifications.meta.last_page) {
        page = this.sentNotifications.meta.last_page;
      }
      if (!page || page < 1) {
        page = 1;
      }
      this.page = page;
      this.loadSentNotifications();
    },
    nextPage() {
      if (this.page >= this.sentNotifications.meta.last_page) {
        return;
      }
      this.page += 1;
      this.loadSentNotifications();
    },
    previousPage() {
      if (this.page === 1) {
        return;
      }
      this.page -= 1;
      this.loadSentNotifications();
    },
    showResendNotificationModal(sentNotification) {
      if (sentNotification.contact.phone) {
        this.$refs.notificationSendModal.setRecipients({ contacts: [sentNotification.contact] });
      } else {
        this.$refs.notificationSendModal.setRecipients(sentNotification.bulk_data);
      }

      this.$refs.notificationSendModal.setTemplate(sentNotification.template_message, sentNotification.meta_data.tags);
      this.openSendNotificationModal();
    },
    openSendNotificationModal() {
      if (this.hasSendNotificationPermission) {
        this.$refs.notificationSendModal.show();
      } else {
        this.$refs.permissionInfoModal.show();
      }
    },
    setStatusClass(status) {
      return {
        success: status === 'read',
        secondary: status === 'delivered',
        danger: status === 'failed',
      };
    },
    showProgressBar() {
      const userInitiatedBulkSend = localStorage.getItem(`_c_gm_pf_ns.${this.$route.params.merchant_id}`);
      if (userInitiatedBulkSend && this.sentNotifications.data) {
        const targetBulk = this.sentNotifications.data.find(notification => notification.status === 'sending' && (notification.id === userInitiatedBulkSend));
        if (targetBulk) {
          // eslint-disable-next-line no-underscore-dangle
          targetBulk._showDetails = true;
        }
      }
    },
    redirectToDetails(row, index) {
      if (row.bulk) {
        this.$router.push({ name: 'CampaignDetails', params: { campaign_id: row.id } });
      } else {
        // only show notification details
        // eslint-disable-next-line no-underscore-dangle
        this.sentNotifications.data[index]._showDetails = !this.sentNotifications.data[index]._showDetails;
      }
    },
    sent(campaignDetailsResData) {
      return campaignDetailsResData.sent + campaignDetailsResData.delivered + campaignDetailsResData.read + campaignDetailsResData.failed;
    },
    delivered(campaignDetailsResData) {
      return campaignDetailsResData.delivered + campaignDetailsResData.read;
    },
  },
};
</script>
